import axiosInstance from '../api/axios';
import type {
  TraderPayoutOutLimits,
  TraderPayoutOutEnabledRequest,
  PaymentSystemPayoutOutSettingsCreateRequest,
  PaymentSystemPayoutOutSettings,
  PaymentSystemPayoutOutSettingsUpdateRequest,
  PaginatedPayoutInvoicesResponse,
  PayoutInvoiceStatusCountsResponse,
  PayoutInvoicesParams,
  PayoutInvoiceRequisitesVerdictRequest,
  PayoutInvoiceRequisitesVerdictResponse,
  PayoutStatisticDaily,
  PayoutStatisticParams,
  PayoutStatisticSummary,
} from '../../types/payout';

function buildPayoutInvoicesQueryString(params?: PayoutInvoicesParams): string {
  const sp = new URLSearchParams();
  if (params?.status?.length) {
    for (const s of params.status) {
      sp.append('status', s);
    }
  }
  if (params?.payout_id) {
    sp.set('payout_id', params.payout_id);
  }
  if (params?.trader_id) {
    sp.set('trader_id', params.trader_id);
  }
  if (params?.limit !== undefined) {
    sp.set('limit', String(params.limit));
  }
  if (params?.offset !== undefined) {
    sp.set('offset', String(params.offset));
  }
  return sp.toString();
}

export const payoutsService = {
  claimPayoutInvoice: async (payoutId: string) => {
    const response = await axiosInstance.post(`/payouts/invoices/${payoutId}/claim`);
    return response.data;
  },

  releasePayoutInvoice: async (payoutId: string) => {
    const response = await axiosInstance.delete(`/payouts/invoices/${payoutId}/claim`);
    return response.data;
  },

  resetPayoutInvoiceWorkState: async (payoutId: string) => {
    const response = await axiosInstance.delete(`/payouts/invoices/${payoutId}/work-state`);
    return response.data;
  },
  /**
   * Список заявок payout_invoices: сортировка по date_create убыв., постранично.
   * Без `status` — все статусы; с массивом — только указанные (`?status=A&status=B`).
   * Опционально: `payout_id`, `trader_id`.
   */
  getPayoutInvoices: async (params?: PayoutInvoicesParams): Promise<PaginatedPayoutInvoicesResponse> => {
    const qs = buildPayoutInvoicesQueryString(params);
    const url = qs ? `/payouts/invoices?${qs}` : '/payouts/invoices';
    const response = await axiosInstance.get<PaginatedPayoutInvoicesResponse>(url);
    return response.data;
  },

  getPayoutInvoiceStatusCounts: async (): Promise<PayoutInvoiceStatusCountsResponse> => {
    const response = await axiosInstance.get<PayoutInvoiceStatusCountsResponse>(
      '/payouts/invoices/status-counts',
    );
    return response.data;
  },

  getPayoutVideoAccess: async (
    payoutId: string,
  ): Promise<{ url: string; expires_at: string }> => {
    const response = await axiosInstance.get<{ url: string; expires_at: string }>(
      `/payouts/invoices/${payoutId}/video-access`,
    );
    const apiBaseUrl = axiosInstance.defaults.baseURL;
    if (!apiBaseUrl) {
      throw new Error('API base URL is not configured');
    }
    return {
      ...response.data,
      url: new URL(response.data.url, apiBaseUrl).toString(),
    };
  },

  /**
   * Вердикт по реквизитам заявки (только UNDER_REVIEW). Тот же `axiosInstance`, что и у остальных `/payouts/*`.
   */
  postPayoutInvoiceRequisitesVerdict: async (
    payoutId: string,
    payload: PayoutInvoiceRequisitesVerdictRequest,
  ): Promise<PayoutInvoiceRequisitesVerdictResponse> => {
    const response = await axiosInstance.post<PayoutInvoiceRequisitesVerdictResponse>(
      `/payouts/invoices/${payoutId}/requisites-verdict`,
      payload,
    );
    return response.data;
  },

  /**
   * Включает/выключает только `payout_out_enabled`; прочие лимиты на бэкенде не меняются.
   * Возвращает полную карточку лимитов для обновления UI.
   */
  setTraderPayoutOutEnabled: async (
    traderId: string,
    payload: TraderPayoutOutEnabledRequest,
  ): Promise<TraderPayoutOutLimits> => {
    const response = await axiosInstance.patch<TraderPayoutOutLimits>(
      `/payouts/traders/${traderId}/payout-out-enabled`,
      payload,
    );
    return response.data;
  },

  /**
   * Создаёт настройки OUT для выбранной ПС (одна строка на ПС; повтор → 409).
   * ПС должна иметь supports_payout_out=true, иначе 400.
   */
  createPaymentSystemPayoutOutSettings: async (
    payload: PaymentSystemPayoutOutSettingsCreateRequest,
  ): Promise<PaymentSystemPayoutOutSettings> => {
    const response = await axiosInstance.post<PaymentSystemPayoutOutSettings>(
      '/payouts/payment-system-settings',
      payload,
    );
    return response.data;
  },

  /**
   * Настройки OUT по ПС. Если записи нет — 404.
   */
  getPaymentSystemPayoutOutSettingsByPaymentSystem: async (
    paymentSystemId: string,
  ): Promise<PaymentSystemPayoutOutSettings> => {
    const response = await axiosInstance.get<PaymentSystemPayoutOutSettings>(
      `/payouts/payment-system-settings/by-payment-system/${paymentSystemId}`,
    );
    return response.data;
  },

  /**
   * Полная замена трёх TTL по ПС. Если настроек нет — 404.
   */
  updatePaymentSystemPayoutOutSettingsByPaymentSystem: async (
    paymentSystemId: string,
    payload: PaymentSystemPayoutOutSettingsUpdateRequest,
  ): Promise<PaymentSystemPayoutOutSettings> => {
    const response = await axiosInstance.put<PaymentSystemPayoutOutSettings>(
      `/payouts/payment-system-settings/by-payment-system/${paymentSystemId}`,
      payload,
    );
    return response.data;
  },

  getStatisticSummary: async (
    params?: PayoutStatisticParams,
  ): Promise<PayoutStatisticSummary> => {
    const response = await axiosInstance.get<PayoutStatisticSummary>(
      '/payouts/statistic/summary',
      { params },
    );
    return response.data;
  },

  getStatisticDaily: async (
    params: PayoutStatisticParams & { from_date: string; to_date: string },
  ): Promise<PayoutStatisticDaily> => {
    const response = await axiosInstance.get<PayoutStatisticDaily>(
      '/payouts/statistic/daily',
      { params },
    );
    return response.data;
  },
};
